import { makeStyles } from '@material-ui/core/styles';
import { Box } from '@material-ui/core';
import { platformTokens } from '@roc/feature-app-core';

// tokens are read inside makeStyles (not at module load) because of a circular import with feature-app-core
const useStyles = makeStyles(theme => {
  const { color: platformColor, radius } = platformTokens;
  const divider = platformColor.neutral100;
  return {
    container: {
      width: '100%',
      border: `1px solid ${divider}`,
      borderRadius: radius.lg,
      overflow: 'hidden',
      backgroundColor: platformColor.surface,
    },
    // Tiles wrap to as many columns as fit, so any number of fields works; auto-fit stretches them to fill a short row.
    // The negative margin hides the dividers on the outer edges (clipped by the container).
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      marginRight: -1,
      marginBottom: -1,
    },
    tile: {
      minWidth: 0,
      padding: theme.spacing(1.5, 2),
      boxShadow: `1px 0 0 ${divider}, 0 1px 0 ${divider}`,
    },
    fullRow: {
      gridColumn: '1 / -1',
    },
    label: {
      fontSize: 13,
      lineHeight: '18px',
      fontWeight: 500,
      color: platformColor.subtle,
      marginBottom: theme.spacing(0.5),
    },
    value: {
      fontSize: 20,
      lineHeight: '28px',
      fontWeight: 600,
      color: platformColor.heading,
      overflowWrap: 'anywhere',
      '& .MuiTypography-root': {
        fontSize: 'inherit',
        fontWeight: 'inherit',
      },
    },
  };
});

export interface DetailsTableField {
  label: string;
  value: any;
  colSpan: 1 | 2;
}

interface DetailsTableProps {
  fields: DetailsTableField[];
}

export const DetailsTable = ({ fields }: DetailsTableProps) => {
  const classes = useStyles();

  return (
    <Box className={classes.container}>
      <Box className={classes.grid}>
        {fields.map(field => (
          <Box
            key={field.label}
            className={
              field.colSpan === 2
                ? `${classes.tile} ${classes.fullRow}`
                : classes.tile
            }
          >
            <Box className={classes.label}>{field.label}</Box>
            <Box className={classes.value}>{field.value}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
